import { computed } from 'vue'
import { useCatalogStore } from './store/catalog'
import { useProductAPI, transformProduct, productApi } from './api/product.api'
import { categoryAPI } from './api/category.api'
import type { Product } from './model/types'

export type {
  Product,
  Category,
  ProductUnit,
  ProductReview,
  ProductFilter,
  ProductSearchParams,
  ProductSearchResult,
  Manufacturer,
} from './model/types'

/**
 * Owner-view facade: CatalogView использует широкий surface store.
 * Наружу модуля по-прежнему предпочтительны узкие хелперы ниже.
 */
export { useCatalogStore } from './store/catalog'

// Admin-facing transport contracts (используются modules/admin для CRUD товаров/категорий).
// Публичный re-export вместо прямого импорта из `catalog/api/*` - сам transport/DTO
// маппинг остаётся собственностью catalog-модуля, admin меняться не должен.
export { useProductAPI, productApi }
export { categoryAPI }
export type { AdminProductPayload, Product as AdminProduct } from './api/product.api'
export type {
  Category as AdminCategory,
  CategoryForm,
  CategoryStats,
  CategoryListResponse,
  CategoryResponse,
  CategoryFilters,
  BulkActionRequest,
  BulkActionResponse,
} from './api/category.api'

/**
 * Presentation-компонент карточки товара - единственная точка, через которую
 * другие модули (profile/favorites, common/home) должны получать ProductCard,
 * вместо прямого импорта из `modules/catalog/components/*`.
 */
export { default as ProductCard } from './components/ProductCard.vue'

/**
 * Узкий публичный контракт для витринных блоков вне каталога (например,
 * главная страница): список категорий/товаров и их загрузка, без пагинации,
 * фильтров и прочего внутреннего состояния CatalogView.
 */
export function useCatalogHighlights() {
  const catalogStore = useCatalogStore()

  return {
    categories: computed(() => catalogStore.categories),
    products: computed(() => catalogStore.products),
    fetchCategories: () => catalogStore.fetchCategories(),
    fetchProducts: () => catalogStore.fetchProducts(),
  }
}

/**
 * Стабильный UI-DTO популярных товаров для витрин вне каталога (favorites
 * recommendations, home). Транспортный shape и Money-нормализация остаются
 * внутри catalog/api - наружу отдаётся готовый Product.
 */
export async function getRecommendedProducts(limit = 4): Promise<Product[]> {
  const { getPopularProducts } = useProductAPI()
  const popular = await getPopularProducts(limit)
  return popular.map((p) => transformProduct(p as unknown as Record<string, unknown>) as Product)
}
